import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

export default function MaintenanceBanner() {
  const [targetTime, setTargetTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const handleScheduled = (e) => {
      setTargetTime(e.detail);
    };
    
    const handleCancelled = () => {
      setTargetTime(null);
      setTimeLeft(null);
    };

    window.addEventListener('app:maintenance_scheduled', handleScheduled);
    window.addEventListener('app:maintenance_cancelled', handleCancelled);

    return () => {
      window.removeEventListener('app:maintenance_scheduled', handleScheduled);
      window.removeEventListener('app:maintenance_cancelled', handleCancelled);
    };
  }, []);

  useEffect(() => {
    if (!targetTime) return;

    const updateTimer = () => {
      const now = Date.now();
      const diff = targetTime - now;
      if (diff <= 0) {
        setTimeLeft('00:00');
        // Instantly trigger maintenance mode lockout
        window.dispatchEvent(new CustomEvent('app:maintenance'));
        setTargetTime(null);
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [targetTime]);

  if (!targetTime || !timeLeft) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      background: 'rgba(20, 20, 20, 0.85)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(239, 68, 68, 0.4)',
      borderRadius: 'var(--radius-pill, 100px)',
      color: '#fff',
      padding: '8px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      fontWeight: 500,
      fontSize: 14,
      boxShadow: '0 8px 32px rgba(239, 68, 68, 0.2), 0 0 0 1px rgba(239, 68, 68, 0.1) inset',
      animation: 'slideDownBanner 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
    }}>
      <style>{`
        @keyframes slideDownBanner {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(239, 68, 68, 0.5); }
          50% { box-shadow: 0 0 24px rgba(239, 68, 68, 0.8); }
        }
      `}</style>
      
      <div style={{ 
        width: 28, height: 28, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.15)', 
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' 
      }}>
        <AlertTriangle size={16} />
      </div>
      
      <span>
        System maintenance starting in{' '}
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: 15, 
          background: '#ef4444', 
          color: '#fff',
          padding: '2px 8px', 
          borderRadius: 6,
          fontWeight: 700,
          marginLeft: 4,
          marginRight: 4,
          display: 'inline-block',
          animation: 'pulseGlow 2s infinite'
        }}>
          {timeLeft}
        </span>
        . Please log out immediately
      </span>
    </div>
  );
}
